import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Activity,
  Wind,
  AlertTriangle,
  Sparkles,
  Loader2,
  Droplet,
  Flame,
  CheckCircle2
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsPneumologiePage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (protocolId, protocolName, content) => {
    setAiLoading({ ...aiLoading, [protocolId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_pneumologie',
          audience: 'adult',
          prompt: `Analyse pneumologique pour ${protocolName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Reconnaissance des critères de gravité
2. Priorités thérapeutiques immédiates
3. Indications de ventilation (VNI, IOT)
4. Surveillance et ajustements
5. Complications à anticiper`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [protocolId]: data.response });
        toast.success(`Analyse AI ${protocolName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [protocolId]: false });
    }
  };

  const protocols = [
    {
      id: 'asthme-grave',
      name: 'Asthme Aigu Grave',
      icon: Wind,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**ASTHME AIGU GRAVE**

**DÉFINITION:**
Exacerbation d'asthme sévère susceptible de mettre en jeu le pronostic vital et/ou nécessitant une prise en charge urgente.

**ÉLÉMENTS DE GRAVITÉ**

• Antécédent d'hospitalisation et de recours à la ventilation mécanique pour asthme
• Prise récente de corticoïdes oraux
• Consommation importante ou croissante de bêta-2 mimétiques
• Âge > 70 ans
• Difficulté à parler
• Troubles de la conscience
• État de choc
• Fréquence respiratoire > 30 cycles/min
• Existence d'arguments en faveur d'une pneumopathie infectieuse
• Normo ou hypercapnie
• Terrain:
  - Poly sensibilisation allergique
  - Asthme insuffisamment traité ou mal contrôlé
  - Antécédents d'hospitalisation pour asthme
  - Tabagisme passif

**CONDUITE À TENIR**

**Mesures initiales:**
• Position demi-assis
• Scope monitoring
• **O2 titré QSP SpO2 94-98%**

**Traitement bronchodilatateur:**
• **Aérosols répétés Terbutaline ou Salbutamol 5 mg en continu sur une heure**
• Sous O2 si hypoxémie
• **Aérosol de bromure d'ipratropium:**
  - 0,5 mg si > 6 ans (toutes les 8 heures)
  - 0,25 mg si < 6 ans (toutes les 8 heures)

**Corticothérapie (dans l'heure):**
• **2 mg/kg d'équivalent prednisone**
• Maximum 80 mg
• Voie IV ou per os (PO)

**Antibiothérapie:**
• Pas d'ATB systématique

**EN CAS D'ÉCHEC DU TRAITEMENT MÉDICAL**
Ou présentation clinique grave d'emblée (troubles de conscience, bradypnée):

• **Sulfate de magnésium** (non systématique, à discuter chez les plus graves):
  - 2 g en 20 min en perfusion

• **Poursuite des aérosols bêta-2 mimétiques en continu**

• **En dernier recours - INTUBATION:**
  - Pré-oxygénation VNI
  - ISR: **Suxamethonium 1 mg/kg** + **Kétamine 3-5 mg/kg**
    OU **Propofol 1,5-2,5 mg/kg**
  - Poursuite aérosols sur circuit de ventilation

**VENTILATION MÉCANIQUE**

**Paramètres:**
• VAC avec VT à 6 mL/kg
• PEP à 3 cmH2O
• I/E à 1/2
• Surveillance P° plateau < 30 cmH2O
• FR selon l'âge
• FiO2 QSP SpO2 > 90%

**Surveillance:**
⚠ **Attention au pneumothorax compressif** (pouvant passer inaperçu, écho ++)
• Drainage pleural à envisager en pré-hospitalier si nécessaire
• Matériel: aiguille simple, drain thoracique, cathéter central mono-voie

**SÉDATION**

• Sédation profonde Ramsay 6 (cf protocole sédation)
• Curarisation

**Options supplémentaires:**
• **Salbutamol IVSE** (efficacité non prouvée):
  - Débuter à 0,5 mg/h
  - Adaptation fonction FC < 120-130/min et bronchospasme

• **Kétamine à l'entretien:**
  - 1-2 mg/kg/h
  - ⚠ Attention effets indésirables: tachycardie, hypersialorrhée, IDM

**ORIENTATION**

• Admission réanimation/USC au cas par cas selon gravité`
    },
    {
      id: 'bpco',
      name: 'Exacerbation de BPCO',
      icon: Activity,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**EXACERBATION DE BPCO**

**DIAGNOSTIC**

• **Exacerbation BPCO** = Aggravation aiguë des symptômes respiratoires:
  - Toux
  - Expectoration
  - Dyspnée

• **Décompensation** = Exacerbation avec insuffisance respiratoire aiguë

• **En pratique:** Insuffisance respiratoire aiguë avec bronchospasme

**ÉLÉMENTS DE GRAVITÉ**

**Signes cliniques d'hypercapnie:**
• Troubles de conscience
• Agitation
• Sueurs
• Flapping
• HTA

**Signes d'insuffisance respiratoire aiguë:**
• Incapacité à parler
• Tirage
• Cyanose
• FR ≥ 30/min
• Respiration paradoxale

**Autres critères:**
• Instabilité hémodynamique
• Épuisement respiratoire:
  - Troubles de conscience
  - Cyanose
  - Bradypnée

**CONDUITE À TENIR**

**Traitement médical:**

**Mesures générales:**
• Position demi-assise
• VVP
• **O2 titré QSP saturation entre 88 et 92%**
• Aérosols à réaliser en priorité sous air si possible

**Bronchodilatateurs:**
• **Nébulisations répétées de Salbutamol (ou Terbutaline) 5 mg**
• Jusqu'à amélioration des symptômes
• **Association possible avec Ipratropium 0,5 mg**
  - Minimum 0,5 mg/8h

**Corticothérapie:**
• **Corticoïdes per os ou IV**
• **0,5 mg/kg**
• Maximum 40 mg

**Antibiothérapie:**
• Pas d'indication pré-hospitalière

**Recherche de complications:**
• Recherche d'une complication traitable (pneumothorax)

**INDICATIONS DE VNI**

Début en Mode AI + PEP (cf protocole mise en route VNI)

**Indications:**
• Signes cliniques de détresse majeure initiale
• Encéphalopathie hypercapnique
• Résistance au traitement médical initial
• Décompensation avec acidose respiratoire pH < 7,35

**EN L'ABSENCE de contre-indication**

**INDICATIONS DE VENTILATION MÉCANIQUE**

Si contre-indications ou échec de VNI:
• Cf protocole poumon pathologique

**ORIENTATION**

• Réanimation ou déchocage selon les cas`
    },
    {
      id: 'dyspnee-inspiratoire',
      name: 'Dyspnée Inspiratoire',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**DYSPNÉE INSPIRATOIRE**

**DIAGNOSTIC**

• Tirage inter-costal, sus-claviculaire
• Dysphonie souvent associée en cas d'origine laryngée

**ÉTIOLOGIES**

**Allergiques:**
• Œdème de Quincke avec œdème de la glotte
• Souvent dans un contexte de choc anaphylactique

**Mécaniques:**
• Inhalation d'un corps étranger

**Infectieuses (surtout enfant):**
• **Épiglottite:**
  - Enfant non vacciné
  - De plus en plus fréquent chez l'adulte (4/an sur St Brieuc)
  - Tableau de dysphonie/dyspnée fébrile à CRP haute
  - La dyspnée est un facteur de risque chez l'adulte
• **Laryngite**

**Trachéales:**
• Sténose tumorale endoluminale ou extraluminale
• Granulome post-intubation

**Autres:**
• Traumatique
• Brûlure des voies aériennes supérieures

**ÉLÉMENTS DE GRAVITÉ**

• Sueurs
• Tachypnée ou bradypnée marquée
• Tirage inspiratoire:
  - Sus-claviculaire
  - Sus-sternal
  - Intercostal
  - Sous-sternal
• **Stridor**
• Agitation, prostration, coma
• Diminution des mouvements thoraciques respiratoires
• Diminution du murmure vésiculaire
• Augmentation du temps inspiratoire

**CONDUITE À TENIR**

**Mesures générales:**
• Position demi-assise
• **O2**
• **Respecter au maximum la ventilation spontanée (retarder IOT)**
• Orientation vers bloc ORL pour:
  - Fibroscopie vigile
  - Voire intubation ou trachéotomie en urgence

**Traitement:**
• **Discuter aérosol adrénaline 0,1 mg/kg (max 5 mg)**
  - Sauf corps étranger

**En cas d'extrême urgence:**
• Si possible intubation avec petite sonde
• ⚠ Risque d'intubation difficile ++ (cf protocole intubation difficile)
• Envisager abord sous-glottique si intubation et ventilation impossibles
  - Cricothyroïdotomie (cf protocole cricothyroïdotomie)

**MESURES SPÉCIFIQUES**

**Tumeur laryngée:**
• **Methylprednisolone (Solumédrol®) 120 mg**

**Corps étranger:**
• Manœuvres de désobstruction en cas d'obstruction complète
• Si nécessaire:
  - Désobstruction à la pince de Magill
  - Intubation
  - Voire cricothyroïdotomie

**Œdème de Quincke:**
• **Adrénaline IM 0,01 mg/kg (max 0,5 mg)**
• Éviction de l'allergène (cf protocole anaphylaxie)
• **Aérosols d'adrénaline 0,1 mg/kg max 5 mg**

**Ingestion de caustique:**
• Cf protocole Caustiques

**Épiglottite de l'enfant:**
• Cf protocole épiglottite

**ORIENTATION**

• Surveillance hospitalière de 2 à 3 heures si aérosol adrénaline
• Rapprochement du service pédiatrie selon gravité`
    },
    {
      id: 'hemoptysie',
      name: 'Hémoptysie',
      icon: Droplet,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**HÉMOPTYSIE**

**DIAGNOSTIC**

• Expectoration de sang au cours d'un effort de toux

**Diagnostic différentiel:**
• Épistaxis
• Hématémèse

**Note:**
• Multiples étiologies
• Seules les hémoptysies menaçant le pronostic vital sont traitées ici

**ÉLÉMENTS DE GRAVITÉ**

**Quantification du volume:**
• Pas de valeur seuil consensuel
• Ordre de grandeur grave si **> 200 mL** (équivalent d'un verre)

**Critères de sévérité:**
• **Insuffisance respiratoire aiguë** (surtout)
• Instabilité hémodynamique (à un moindre degré)

**MISE EN CONDITION**

• Scope monitoring
• O2
• **Lunettes + FFP2 pour soignants**
• Masque chirurgical pour patient selon tolérance
• **2 VVP NaCl**
• Bilan avec Groupe-RAI, Hémocue

**Positionnement:**
• **Décubitus latéral du côté suspecté pathologique**
• Selon clinique et radio thorax
• Discuter intubation sélective à droite si localisation lésion hémorragique suspectée à gauche

**TRAITEMENT**

**Patient instable:**
• **Intubation sonde de bon calibre (≥ 8)**
• Principe de ventilation du SDRA (cf protocole ventilation)
• Penser à augmenter la PEEP si suspicion de saignement artériolaire

**Correction des troubles de coagulation:**
Selon les cas:
• Vitamine K
• PPSB
• Praxbind
• Plaquettes

**Traitement hémostatique:**
• Transfusion
• **Acide tranexamique 1 g en 10 min**
  - À ne débuter que si le saignement a débuté depuis < 3h

**Examens/Interventions:**
• Angioscanner thoracique pour embolisation
• Et/ou fibroscopie souple

**Dernier recours - Terlipressine (hors AMM):**
Si hémoptysie d'emblée menaçante ou échec traitements locaux:
• **Bolus initial 2 mg IVD** (1 mg si < 50 kg)
• Puis éventuellement 1 mg toutes les 4 à 6 heures
• ⚠ Déconseillé en cas de pathologie ischémique sous-jacente
  - Cf fiche terlipressine

**ORIENTATION**

• **Transport médicalisé**

**Hémoptysie menaçant le pronostic vital:**
• Après régulation
• Vers plateau technique proposant fibroscopie et embolisation
  - CHU Rennes et Brest
• Après accord réanimateur médical

**Contact radiologie interventionnelle CHU Rennes:**
• 02 99 28 43 21 poste 85301 / 86080`
    },
    {
      id: 'pneumothorax-compressif',
      name: 'Pneumothorax Compressif',
      icon: Flame,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**PNEUMOTHORAX COMPRESSIF**

**DIAGNOSTIC**

**Signes respiratoires:**
• Détresse respiratoire aiguë
• Distension thoracique unilatérale
• Immobilité du thorax
• Tympanisme à la percussion
• Silence auscultatoire
• Au maximum: déviation trachéale

**Signes hémodynamiques:**
• État de choc obstructif
• Déplacement des bruits du cœur
• Turgescence jugulaire (tamponnade gazeuse)

**Confirmation:**
• Possibilité de confirmation échographique

**CONDUITE À TENIR**

**⏱ EXSUFFLATION +++**

• **TOUJOURS suivie d'un drainage**
• L'exsufflation ne traite pas le pneumothorax
• Permet de lever la distension pleurale et les signes compressifs

**REPÈRES ANATOMIQUES**

Dans la zone du tympanisme, ponction sur le bord supérieur de la côte inférieure:

**Abord axillaire (recommandé):**
• Exsufflation et drainage au **5e espace intercostal**
• Environ à hauteur de la ligne mamelonnaire
• Légèrement en avant de la ligne médio-axillaire

**Abord antérieur:**
• 2e ou 3e espace intercostal
• Sur la ligne médio-claviculaire
• Perpendiculaire à l'axe de la clavicule
• Perpendiculaire au plan cutané
• ⚠ Risque d'échec plus élevé (selon épaisseur du plan musculo sous-cutané)

**EXSUFFLATION SEULE**

• Passage d'un cathlon ou aiguille de grande taille (au moins 5 cm)
• Monté sur une seringue
• Ponction sur le bord supérieur de la côte inférieure
• Progression vide à la main jusqu'à la ponction d'air
• Désadapter la seringue pour laisser l'air en surpression s'évacuer

**EXSUFFLATION/DRAINAGE PAR TECHNIQUE DE SELDINGER**

Cathéter en "queue de cochon" ou cathéter de voie veineuse centrale (possible en pré-hospitalier):

**Procédure:**
• En conditions d'asepsie chirurgicale
• Anesthésie locale chez patient conscient
• Ponction à l'aiguille vide à la main jusqu'à l'obtention d'air
• Montage du guide dans l'aiguille puis ablation de l'aiguille
• Ouverture cutanée 5 mm en regard du guide
• Passage puis retrait du dilatateur
• Passage du cathéter sur le guide
• Montée sur valve anti-retour si système prévu
• Sur robinet 3 voies en pré-hospitalier avec évacuation d'air régulière sinon

**DRAINAGE THORACIQUE**

**Conditions:**
• En conditions d'asepsie chirurgicale
• Anesthésie locale chez patient conscient

**Calibre du drain:**
• **12 à 20 Fr** pour drainage de pneumothorax
• **24 à 36 Fr** pour hémothorax
• Drain à adapter à la taille de l'enfant si pédiatrie
  - Cf échelle Broselow
  - Cf protocole constantes pédiatriques

**Technique:**
• Ouverture cutanée sur 2 cm environ

**POINTS CLÉS**

✓ Urgence vitale absolue
✓ Exsufflation en urgence
✓ Toujours suivie d'un drainage
✓ Abord axillaire préférable
✓ Asepsie stricte`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="referentiels-pneumo-container">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-400/30">
            <Wind className="w-8 h-8 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Référentiels Pneumologie
            </h1>
            <p className="text-[hsl(var(--muted-2))] mt-1">
              Protocoles urgences respiratoires - SMUR 2025
            </p>
          </div>
        </div>

        {/* Alert */}
        <Alert className="border-cyan-400/50 bg-cyan-500/10">
          <Wind className="h-5 w-5 text-cyan-400" />
          <AlertDescription className="text-cyan-300 font-medium">
            <strong>Urgences respiratoires:</strong> Évaluation rapide de la gravité • Oxygénothérapie adaptée • Bronchodilatateurs précoces • Intubation si épuisement • VNI selon indications
          </AlertDescription>
        </Alert>
      </div>

      {/* Protocols Card */}
      <Card className="border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Activity className="w-6 h-6 text-cyan-400" />
            Protocoles Pneumologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher les recommandations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {protocols.map((protocol) => {
              const Icon = protocol.icon;
              const isLoading = aiLoading[protocol.id];
              const aiResult = aiResults[protocol.id];

              return (
                <AccordionItem 
                  key={protocol.id} 
                  value={protocol.id}
                  className={`border ${protocol.border} ${protocol.bg} rounded-lg overflow-hidden`}
                  data-testid="protocol-item"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <div className={`p-2 rounded-lg ${protocol.bg} border ${protocol.border}`}>
                        <Icon className={`w-5 h-5 ${protocol.color}`} />
                      </div>
                      <span className="font-semibold text-lg">{protocol.name}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-6 pb-6 space-y-6">
                      <ScrollArea className="h-auto max-h-[600px]">
                        <div className="prose prose-invert max-w-none pr-4">
                          <div className="text-[hsl(var(--text))] space-y-4">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                const text = line.replace(/\*\*/g, '');
                                if (line.includes('DÉFINITION') || line.includes('TRAITEMENT') || line.includes('CONDUITE')) {
                                  return <h3 key={idx} className="text-xl font-bold text-cyan-400 mt-6 mb-3">{text}</h3>;
                                }
                                return <h4 key={idx} className="text-lg font-semibold text-cyan-300 mt-4 mb-2">{text}</h4>;
                              }
                              if (line.trim().startsWith('•')) {
                                return <li key={idx} className="ml-4 text-[hsl(var(--text))] leading-relaxed">{line.replace('•', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('-')) {
                                return <li key={idx} className="ml-8 text-[hsl(var(--muted-2))] text-sm leading-relaxed list-disc">{line.replace('-', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('✓')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/30 my-2">
                                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-cyan-300 font-medium">{line.replace('✓', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('⚠')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-orange-300 font-medium">{line.replace('⚠', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('⏱')) {
                                return (
                                  <div key={idx} className="flex items-center gap-2 p-3 bg-red-500/10 rounded-lg border border-red-400/30 my-2">
                                    <Activity className="w-5 h-5 text-red-400 flex-shrink-0" />
                                    <span className="text-red-300 font-bold text-lg">{line.replace('⏱', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim()) {
                                return <p key={idx} className="text-[hsl(var(--text))] leading-relaxed">{line}</p>;
                              }
                              return <div key={idx} className="h-2" />;
                            })}
                          </div>
                        </div>
                      </ScrollArea>

                      <Button
                        onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                        data-testid={`ai-button-${protocol.id}`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyse AI en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Analyse AI de ce protocole
                          </>
                        )}
                      </Button>

                      {aiResult && (
                        <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                          <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Analyse AI
                          </h3>
                          <div className="prose prose-invert max-w-none">
                            <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                              {aiResult}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
